import React, {useRef, useEffect} from "react";
import {useSelector} from "react-redux";
import PropTypes from "prop-types";
import {useFormikContext} from "formik";
import {
  newIsFieldRequired,
  newGetFieldLabel,
  newGetFieldPlaceholder,
  newGetSelectFieldOptions,
  useAmsSelector
} from "@awe-web/shared/lib";
import {Translation as T, useTranslation} from "@awe-web/shared/lib";
import {provisioningTypes} from "@awe-web/shared/lib";
import {Row, Col} from "../../../components/grid.js";
import { ScreenHeading, ScreenSectionHeading } from "../../../components/screen.js";
import { Button, FormikLabeledField, FormikTextInput, FormikSelect } from "../../../components/form.js";
import {getCoStation} from "../../../stations/store.js";
import { getFormattedPickupPrice, getFormattedDeliveryPrice, getIsDeliveryEnabled } from "../../../rent_contracts/store.js";
import {ProvisioningSelection} from "../../../rent_contracts/components/provisioning_selection.js";
import {StationAddress} from "../../../stations/components/station_card";

const SCREEN_HEADING = "ALG0000257";
const PROVISIONING_HEADING = "ALG0000067";
const OPTION_PICKUP = "ALG0000070";
const OPTION_DELIVERY_TO_CUSTOM_ADDRESS = "ALG0000258";
const IMAGE ="ALG0000247";
const SUBMIT = "ALG0000072";

export default function SelectProvisioningForm({values: {provisioningType}, handleSubmit}) {
  const formik = useFormikContext();
  const {translate} = useTranslation();

  const coStation = useSelector((state) => getCoStation(state));
  const formattedDeliveryPrice = useSelector((state) => getFormattedDeliveryPrice(state));
  const formattedPickupPrice = useSelector((state) => getFormattedPickupPrice(state));
  const isDeliveryEnabled = useSelector((state) => getIsDeliveryEnabled(state));

  const isDeliveryFieldRequired = useAmsSelector((state) => newIsFieldRequired("deliveryAddress12")(state));
  const getDeliveryFieldLabel = useAmsSelector((state) => newGetFieldLabel("deliveryAddress12")(state));
  const getDeliveryFieldPlaceholder = useAmsSelector((state) => newGetFieldPlaceholder("deliveryAddress12")(state));
  const getDeliverySelectFieldOptions = useAmsSelector((state) => newGetSelectFieldOptions("deliveryAddress12")(state));

  // Store formik in ref to avoid being forced to put it in the dependency
  // array which runs the effect below more often than it needs to.
  const formikRef = useRef();
  formikRef.current = formik;

  // Untouch fields in delivery form every time it is opened in order to remove
  // previous error messages.
  useEffect(() => {
    if (provisioningType === provisioningTypes.DELIVERY_TO_CUSTOM_ADDRESS) {
      [
        "DeliveryContactPerson:name",
        "DeliveryContactPerson:surname",
        "DeliveryAddress:street",
        "DeliveryAddress:building",
        "DeliveryAddress:zipCode",
        "DeliveryAddress:city",
        "DeliveryAddress:countryCode",
        "DeliveryContactPerson:phone"
      ]
        .filter((name) => formikRef.current.touched[name])
        .forEach((name) => {
          formikRef.current.setFieldTouched(name, false);
        });
    }
  }, [provisioningType]);

  return (
    <form
      className="b2c-ltr-select-provisioning-form"
      onSubmit={handleSubmit}
    >
      <Row>
        <Col desktop={{cols: 8, offset: 2}}>
          <ScreenHeading><T code={SCREEN_HEADING} /></ScreenHeading>
        </Col>
      </Row>

      <Row className="b2c-ltr-select-provisioning__form-row">
        <Col desktop={{cols: 4, offset: 2}}>
          <ScreenSectionHeading light>
            <T code={PROVISIONING_HEADING} />
          </ScreenSectionHeading>

          <div>
            <ProvisioningSelection
              name="provisioningType"
              value={provisioningTypes.PICKUP}
              label={<T code={OPTION_PICKUP} />}
              formattedPrice={formattedPickupPrice}
            >
              <StationAddress
                className="b2c-ltr-select-provisioning-form__station-address"
                stationNameClassName="b2c-ltr-select-provisioning-form__station-address__name"
                openingTimesHeadingClassName="b2c-ltr-select-provisioning-form__station-address__opening-times-heading"
                station={coStation?.toJS()}
              />
            </ProvisioningSelection>

            <ProvisioningSelection
              name="provisioningType"
              value={provisioningTypes.DELIVERY_TO_CUSTOM_ADDRESS}
              label={<T code={OPTION_DELIVERY_TO_CUSTOM_ADDRESS} />}
              formattedPrice={formattedDeliveryPrice}
              disabled={!isDeliveryEnabled}
            >
              <div
                className="b2c-ltr-select-provisioning-form__fieldset"
              >
                <FormikLabeledField
                  name="DeliveryContactPerson:name"
                  as={FormikTextInput}
                  isFieldRequired={isDeliveryFieldRequired}
                  getFieldLabel={getDeliveryFieldLabel}
                  placeholder={getDeliveryFieldPlaceholder("DeliveryContactPerson:name")}
                />

                <FormikLabeledField
                  name="DeliveryContactPerson:surname"
                  as={FormikTextInput}
                  isFieldRequired={isDeliveryFieldRequired}
                  getFieldLabel={getDeliveryFieldLabel}
                  placeholder={getDeliveryFieldPlaceholder("DeliveryContactPerson:surname")}
                />

                <FormikLabeledField
                  name="DeliveryAddress:street"
                  as={FormikTextInput}
                  isFieldRequired={isDeliveryFieldRequired}
                  getFieldLabel={getDeliveryFieldLabel}
                  placeholder={getDeliveryFieldPlaceholder("DeliveryAddress:street")}
                />

                <FormikLabeledField
                  name="DeliveryAddress:building"
                  as={FormikTextInput}
                  isFieldRequired={isDeliveryFieldRequired}
                  getFieldLabel={getDeliveryFieldLabel}
                  placeholder={getDeliveryFieldPlaceholder("DeliveryAddress:building")}
                />

                <div
                  className="b2c-ltr-select-provisioning-form__zip-code-and-city"
                >
                  <FormikLabeledField
                    className="b2c-ltr-select-provisioning-form__zip-code-and-city__zip-code"
                    name="DeliveryAddress:zipCode"
                    as={FormikTextInput}
                    isFieldRequired={isDeliveryFieldRequired}
                    getFieldLabel={getDeliveryFieldLabel}
                    placeholder={getDeliveryFieldPlaceholder("DeliveryAddress:zipCode")}
                  />

                  <FormikLabeledField
                    className="b2c-ltr-select-provisioning-form__zip-code-and-city__city"
                    name="DeliveryAddress:city"
                    as={FormikTextInput}
                    isFieldRequired={isDeliveryFieldRequired}
                    getFieldLabel={getDeliveryFieldLabel}
                    placeholder={getDeliveryFieldPlaceholder("DeliveryAddress:city")}
                  />
                </div>

                <FormikLabeledField
                  name="DeliveryAddress:countryCode"
                  as={FormikSelect}
                  isFieldRequired={isDeliveryFieldRequired}
                  getFieldLabel={getDeliveryFieldLabel}
                  options={getDeliverySelectFieldOptions("DeliveryAddress:countryCode")}
                />

                <FormikLabeledField
                  name="DeliveryContactPerson:phone"
                  as={FormikTextInput}
                  isFieldRequired={isDeliveryFieldRequired}
                  getFieldLabel={getDeliveryFieldLabel}
                  placeholder={getDeliveryFieldPlaceholder("DeliveryContactPerson:phone")}
                />
              </div>
            </ProvisioningSelection>
          </div>
        </Col>
        <Col className="b2c-ltr-select-provisioning__image-col" desktop={{cols: 4}}>
          <img src={translate(IMAGE)} alt="" />
        </Col>
      </Row>

      <Row className="b2c-ltr-select-provisioning__form-row">
        <Col desktop={{cols: 4, offset: 6}}>
          <Button primary submit continue><T code={SUBMIT} /></Button>
        </Col>
      </Row>
    </form>
  );
}

SelectProvisioningForm.propTypes = {
  values: PropTypes.shape({
    provisioningType: PropTypes.oneOf(Object.values(provisioningTypes))
  }).isRequired,
  handleSubmit: PropTypes.func.isRequired
};
